import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, BookOpen, CheckCircle2, Gift } from "lucide-react";
import { BookCallButton } from "@/components/site/BookCall";
import { api, formatApiError } from "@/lib/api";

export const AVENUE_VALUE = "$5,500";

export function AvenueCard({ a }) {
  const live = a.status === "live";
  const inner = (
    <>
      <p className="font-mono-ui text-[11px] tracking-[0.25em] text-[#F08A3C]">AVENUE {String(a.number).padStart(2, "0")}</p>
      <h3 className="mt-2 text-lg font-bold text-[#0B2A4A]">{a.title}</h3>
      {live ? <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.tagline}</p> : <p className="mt-2 text-sm text-slate-500">Booklet coming soon</p>}
      <p className="mt-auto pt-4 flex items-center justify-between text-sm"><span className="font-semibold text-[#0B2A4A]">${a.price}</span>{live ? <span className="inline-flex items-center gap-1 font-semibold text-[#147C88]">View <ArrowRight className="h-4 w-4" /></span> : <span className="rounded-full bg-[#0B2A4A]/5 px-2.5 py-0.5 text-xs font-semibold text-slate-500">Soon</span>}</p>
    </>
  );
  const cls = `rounded-2xl border p-5 flex flex-col min-h-[200px] ${live ? "bg-white border-[#0B2A4A]/10 hover:border-[#1A9AA8] hover:-translate-y-0.5 transition-transform" : "bg-[#FAF6EE] border-dashed border-[#0B2A4A]/15"}`;
  return live ? <Link to={`/online-courses/${a.slug}`} className={cls} data-testid={`avenue-card-${a.slug}`}>{inner}</Link> : <div className={cls} data-testid={`avenue-card-${a.slug}`}>{inner}</div>;
}

export function useAvenues() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/online-courses").then((r) => setList(r.data)).catch(() => {}); }, []);
  return list;
}

export function RetreatBundle({ testId = "avenues-bundle", showGrid = true }) {
  const list = useAvenues();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14" data-testid={testId}>
      <div className="rounded-[2rem] bg-[#0B2A4A] text-[#F5ECD7] p-6 sm:p-10 grain">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C] flex items-center gap-2"><Gift className="h-4 w-4" /> PART OF THE FULL BEGINNERS COURSE</p>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">11 Avenue courses, free with your 7-day or 30-day retreat</h2>
        <p className="mt-4 max-w-3xl text-[#F5ECD7]/85 leading-relaxed">After the 7 days, every avenue has its own add-on course with digital worksheets, trackers and an Esc resource folder, taken in person or online. Each one is $500 on its own. Join a retreat at Esc Digital Nomad Village and all 11 are included. Courses open on your account once Greg has confirmed your booking and payment.</p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/online-courses" className="inline-flex items-center gap-2 rounded-full bg-[#F08A3C] text-white px-6 py-3.5 font-semibold hover:bg-[#D96F28]" data-testid={`${testId}-cta`}>See all online courses <ArrowRight className="h-4 w-4" /></Link>
          <BookCallButton source="avenues-bundle" light testId={`${testId}-call`} />
          <span className="text-sm text-[#F5ECD7]/70">Value {AVENUE_VALUE} · included with every retreat</span>
        </div>
        {showGrid && list.length > 0 && <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{list.slice(0, 4).map((a) => <AvenueCard key={a.slug} a={a} />)}</div>}
      </div>
    </section>
  );
}

export function MyOnlineCourses() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/me/online-courses").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const choose = (slug) => api.post(`/learn/${slug}/choose`).then(() => { toast.success("Avenue chosen. Parts 2–4 are open."); load(); }).catch((e) => toast.error(formatApiError(e)));
  if (!rows || !rows.some((r) => r.access.has)) return null;
  return (
    <div className="mt-10" data-testid="my-online-courses">
      <h2 className="text-xl font-bold text-[#0B2A4A]">Your Avenue courses</h2>
      <p className="mt-1 text-sm text-slate-600">Courses Greg has unlocked for you. Ask Greg if a part you need is still locked.</p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {rows.filter((r) => r.access.has).map((r) => (
          <div key={r.slug} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5" data-testid={`my-avenue-${r.slug}`}>
            <p className="font-mono-ui text-[11px] tracking-[0.25em] text-[#F08A3C]">AVENUE {String(r.number).padStart(2, "0")}</p>
            <h3 className="mt-1 font-bold text-[#0B2A4A]">{r.title}</h3>
            <p className="text-xs text-slate-500">{r.parts_complete}/4 parts complete · {r.access.full ? "All parts open" : "Part 1 open"}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {r.weeks.map((w) => (r.access.full || w.n === 1
                ? <Link key={w.n} to={`/learn/${r.slug}/${w.n}`} className="inline-flex items-center gap-1 rounded-lg bg-[#0B2A4A] text-[#F5ECD7] px-3 py-1.5 text-xs font-semibold" data-testid={`my-avenue-${r.slug}-part-${w.n}`}><BookOpen className="h-3.5 w-3.5" /> Part {w.n} · {w.name}</Link>
                : <span key={w.n} className="inline-flex items-center gap-1 rounded-lg bg-[#0B2A4A]/5 px-3 py-1.5 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Part {w.n}</span>))}
            </div>
            {r.access.can_choose && <button onClick={() => choose(r.slug)} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#D96F28]" data-testid={`my-avenue-${r.slug}-choose`}><CheckCircle2 className="h-4 w-4" /> I choose this avenue</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function HostAvenueAccess({ userId }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/admin/students/${userId}/online-courses`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (slug, body) => api.put(`/admin/students/${userId}/online-courses/${slug}`, body).then(() => { toast.success("Updated"); load(); }).catch((e) => toast.error(formatApiError(e)));
  if (!rows) return null;
  const btn = "rounded-lg border border-[#0B2A4A]/20 px-2.5 py-1 text-xs font-semibold hover:border-[#1A9AA8]";
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6" data-testid="host-avenue-access">
      <h3 className="font-semibold text-[#0B2A4A]">Avenue online courses</h3>
      <p className="text-sm text-slate-600">Nothing is unlocked automatically. Once payment is confirmed, give Part 1 only or the full course.</p>
      <div className="mt-4 flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.slug} className="rounded-xl border border-[#0B2A4A]/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`host-avenue-${r.slug}`}>
            <div className="text-sm"><p className="font-semibold text-[#0B2A4A]">Avenue {r.number} · {r.title}</p>
              <p className="text-slate-500" data-testid={`host-avenue-status-${r.slug}`}>{!r.access.has ? "Locked" : r.access.full ? "All parts open" : "Part 1 open"} · {r.parts_complete}/{r.parts} complete</p></div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => set(r.slug, { granted: !r.access.full })} className={btn} data-testid={`host-avenue-grant-${r.slug}`}>{r.access.full ? "Remove full course" : "Give full course"}</button>
              <button onClick={() => set(r.slug, { part1: !r.access.part1 })} className={btn} data-testid={`host-avenue-part1-${r.slug}`}>{r.access.part1 ? "Remove Part 1" : "Give Part 1 only"}</button>
              {[1, 2, 3, 4].map((n) => <Link key={n} to={`/host/teach/${userId}/learn/${r.slug}/${n}`} className={`${btn} bg-[#0B2A4A] text-[#F5ECD7]`} data-testid={`host-avenue-teach-${r.slug}-${n}`}>Teach P{n}</Link>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AvenueSettingsAdmin() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/online-courses/settings").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const save = (slug, folder_url) => api.put(`/admin/online-courses/${slug}/settings`, { folder_url }).then(() => toast.success("Resource folder saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="flex flex-col gap-3" data-testid="host-avenue-settings">
      <p className="text-sm text-slate-600">Paste each avenue's Google Drive resource folder link (https://…). Students see a "Resource folder" button in every part.</p>
      {rows.map((r) => (
        <form key={r.slug} onSubmit={(e) => { e.preventDefault(); save(r.slug, e.target.folder.value.trim()); }} className="bg-white rounded-xl border border-[#0B2A4A]/10 p-4 flex flex-wrap items-center gap-3" data-testid={`host-avenue-setting-${r.slug}`}>
          <span className="w-56 text-sm font-semibold text-[#0B2A4A]">Avenue {r.number} · {r.title}{r.status !== "live" && <span className="ml-1 text-xs font-normal text-slate-500">(soon)</span>}</span>
          <input name="folder" defaultValue={r.folder_url} placeholder="https://drive.google.com/…" className="flex-1 min-w-[220px] h-9 rounded-md border px-3 text-sm" data-testid={`host-avenue-folder-${r.slug}`} />
          <button className="rounded-lg bg-[#0B2A4A] text-white px-3 py-1.5 text-xs font-semibold" data-testid={`host-avenue-folder-save-${r.slug}`}>Save</button>
        </form>
      ))}
    </div>
  );
}
